import {
  useCallback,
  ReactNode,
  useRef,
  useEffect,
  Ref,
  ReactElement,
} from "react";
import isEqual from "lodash/isEqual";

export type RadioOptionSpec<T> = {
  value: T;
  content: (selected: boolean, ref?: Ref<HTMLElement>) => ReactNode;
  className?: string;
  tabIndex?: number;
  wantsRef?: boolean;
};

export function RadioOption<T>({
  children,
  value,
  selected,
  onChange,
  onKeyDown,
  className,
  tabIndex,
  divRef,
}: {
  children: ReactNode;
  value: T;
  selected: boolean;
  onChange: (newValue: T) => void;
  onKeyDown: (
    newValue: T,
    keyEvent: React.KeyboardEvent<HTMLInputElement>
  ) => void;
  className?: string;
  tabIndex?: number;
  divRef?: Ref<HTMLElement>;
}): ReactElement {
  const handleClick = useCallback(() => {
    onChange(value);
  }, [value, onChange]);

  const handleKeyDown = useCallback(
    (keyEvent: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown(value, keyEvent);
    },
    [value, onKeyDown]
  );

  let effectiveTabIndex = tabIndex;
  if (typeof effectiveTabIndex === "undefined") {
    effectiveTabIndex = selected ? 0 : -1;
  }

  return (
    <div
      ref={divRef as Ref<HTMLDivElement>}
      role="radio"
      aria-checked={selected}
      tabIndex={effectiveTabIndex}
      onClick={handleClick}
      onMouseDown={handleClick}
      className={className}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}

export function RadioGroup<T>({
  children,
  value,
  onChange,
  ariaLabel,
  ariaRequired,
  className,
  autoFocus,
}: {
  children: RadioOptionSpec<T>[];
  value?: T;
  onChange: (newValue: T) => void;
  ariaLabel?: string;
  ariaRequired?: boolean;
  className?: string;
  autoFocus?: boolean;
}): ReactElement {
  const activeEl = useRef<HTMLElement>(null);

  const handleKeyDown = useCallback(
    (newValue: T, keyEvent: React.KeyboardEvent<HTMLInputElement>) => {
      const index = children.findIndex((spec) => isEqual(newValue, spec.value));
      let delta = 0;
      if (keyEvent.code === "ArrowRight" || keyEvent.code === "ArrowDown") {
        delta = 1;
      } else if (keyEvent.code === "ArrowLeft" || keyEvent.code === "ArrowUp") {
        delta = -1;
      }
      if (delta !== 0) {
        onChange(
          children[(index + children.length + delta) % children.length].value
        );
      }
    },
    [children, onChange]
  );

  useEffect(() => {
    if (activeEl.current && autoFocus) {
      activeEl.current.focus();
    }
  }, [value]);

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-required={ariaRequired}
      className={className}
    >
      {children.map((spec) => {
        const selected = isEqual(value, spec.value);
        return (
          <RadioOption<T>
            key={JSON.stringify(spec.value)}
            value={spec.value}
            selected={selected}
            divRef={selected && !spec.wantsRef ? activeEl : undefined}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            className={spec.className}
            tabIndex={spec.tabIndex}
          >
            {spec.content(
              selected,
              selected && spec.wantsRef ? activeEl : undefined
            )}
          </RadioOption>
        );
      })}
    </div>
  );
}
